<script lang="ts" setup>
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid';
import NewProject from '@/views/search/NewProject.vue';
import { ref, useTemplateRef } from 'vue';
import { type LocationQuery, useRoute } from 'vue-router';
import { getFeaturedProjects, getNewProjects, type GetPresentationConfigurationHomeResponse, type GetPresentationConfigurationSearchResponse } from '@/api';
import { parseNumber, parseString } from '@/utilities';
import { loadPresentationConfiguration } from '@/utilities/presentation-configuration.ts';
import SectionHeader from '@/components/SectionHeader.vue';
import { useI18n } from 'vue-i18n';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  placement: 'home' | 'search';
}>();

const { t } = useI18n();

const route = useRoute();
const newProjectsContainer = useTemplateRef('newProjectsContainer');
const isAtStart = ref(true);
const isAtEnd = ref(false);
const newProjects = ref<number[]>([]);

const fetchFeaturedProjectIds = async (featuredDisplayLimit: number): Promise<number[]> => {
  const fetch = await getFeaturedProjects();

  if (fetch.response?.status === 200 && fetch.data) {
    return fetch.data.items.map((item) => item.id).slice(0, featuredDisplayLimit);
  } else {
    throw Error(`Fetching featured projects failed: ${fetch.error}`);
  }
};

const fetchNewProjects = async (query: LocationQuery): Promise<number[]> => {
  const cityId = parseNumber(query.cityId);
  const regionId = parseNumber(query.regionId);
  const location = parseString(query.location);

  const fetch = await getNewProjects({
    query: {
      cityId: cityId?.toString(),
      regionId: regionId?.toString(),
      location,
    },
  });

  if (fetch.response?.status === 200 && fetch.data) {
    return fetch.data.items;
  } else {
    throw Error(`Fetching search results failed: ${fetch.error}`);
  }
};

const updateScrollIndicators = () => {
  if (newProjectsContainer.value) {
    const container = newProjectsContainer.value;
    isAtStart.value = container.scrollLeft === 0;
    isAtEnd.value = Math.round(container.scrollWidth - container.scrollLeft) === container.clientWidth;
  }
};

const scrollNewProjects = (direction: number) => {
  if (newProjectsContainer.value) {
    const scrollAmount = newProjectsContainer.value.offsetWidth * 0.75;
    newProjectsContainer.value.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth',
    });
  }
};

const loadNewProjects = async () => {
  const presentation = await loadPresentationConfiguration();
  const configuration: GetPresentationConfigurationHomeResponse | GetPresentationConfigurationSearchResponse = presentation[props.placement];
  if (!configuration.newProjectsEnabled) {
    newProjects.value = [];
    return;
  }

  const results = await Promise.allSettled([configuration.newProjectsFeaturedFirst ? fetchFeaturedProjectIds(configuration.newProjectsFeaturedLimit) : Promise.resolve([]), fetchNewProjects(route.query)]);

  const projectIds: number[] = [];
  for (const result of results) {
    if (result.status === 'fulfilled') {
      projectIds.push(...result.value);
    } else {
      console.error(result.reason);
    }
  }

  newProjects.value = [...new Set(projectIds)].slice(0, configuration.newProjectsDisplayLimit);
};

useSsgState({
  key: Object.keys(route.query).length === 0 ? `new-projects:${props.placement}` : null,
  refs: { newProjects },
  isLoaded: () => newProjects.value.length > 0,
  load: loadNewProjects,
});
</script>

<template>
  <section v-if="newProjects.length > 0" class="mb-4">
    <SectionHeader>{{ t('components.new-projects.header') }}</SectionHeader>
    <div class="relative -top-2">
      <div class="no-print absolute top-0 -left-0.5 z-10 flex h-full items-center justify-center bg-linear-to-r from-white to-50% pl-3">
        <button :class="['hover:text-ph-green-300 focus:outline-ph-green-600/20 cursor-pointer rounded-md bg-gray-800/20 py-1 focus:outline-2 focus:outline-offset-2', !isAtStart ? 'text-gray-100' : 'hidden']" @click="scrollNewProjects(-1)">
          <ChevronLeftIcon class="h-10 w-10" />
          <span class="sr-only">
            {{ t('components.new-projects.scroll-left') }}
          </span>
        </button>
      </div>

      <ul ref="newProjectsContainer" class="flex scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-row gap-2 overflow-x-auto scroll-smooth px-1 pt-1 pb-2 md:scrollbar-none" role="list" @scroll="updateScrollIndicators">
        <template v-for="(projectId, index) in newProjects" :key="projectId">
          <NewProject :index="index" :project-id="projectId" />
        </template>
      </ul>

      <div class="no-print absolute top-0 -right-0.5 z-10 flex h-full items-center justify-center bg-linear-to-l from-white to-50% pr-3">
        <button :class="['hover:text-ph-green-300 focus:outline-ph-green-600/20 cursor-pointer rounded-md bg-gray-800/20 py-1 focus:outline-2 focus:outline-offset-2', !isAtEnd ? 'text-gray-100' : 'hidden']" @click="scrollNewProjects(1)">
          <ChevronRightIcon class="h-10 w-10" />
          <span class="sr-only">
            {{ t('components.new-projects.scroll-right') }}
          </span>
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="css" scoped></style>
